import Phaser from "phaser";

/**
 * One Heart — interactive object framework.
 *
 * Every interactive element in the world (chairs, coffee mugs, bar, light
 * switches, closets, etc.) registers as an Interactable with the manager.
 * Each frame the manager:
 *   1. Computes player → object distance for every Interactable.
 *   2. Picks the nearest one within its `radius`.
 *   3. Renders a floating "[E] {label}" hint above it (fading the others out).
 *   4. On E key press, fires that Interactable's `onInteract` callback.
 *
 * Stateful interactions (sit/stand, light on/off) read/write `state` on the
 * Interactable instance. The manager is intentionally dumb about state shape
 * — each callback owns its own logic.
 */

export interface Interactable {
  id: string;
  x: number;
  y: number;
  radius: number;
  label: string;
  onInteract: (ctx: InteractionContext, self: Interactable) => void;
  /** Mutable per-item state — e.g. { sat: false }, { lightOn: true }. */
  state?: Record<string, unknown>;
  /** Optional override: if it returns false, the prompt is hidden but the
   *  object is still registered (e.g. only show "Drink alcohol" when the
   *  user is already sitting at the bar). */
  available?: (ctx: InteractionContext, self: Interactable) => boolean;
  /** Optional override of the displayed label — useful when the label
   *  changes with state (e.g. "Sit" vs "Stand", "Light on" vs "Light off"). */
  dynamicLabel?: (self: Interactable) => string;
}

/**
 * Public surface the manager exposes to interaction callbacks. Adding a new
 * helper here (e.g. `playSound`, `showToast`) makes it available everywhere
 * without each call-site needing to thread the scene reference.
 */
export interface InteractionContext {
  scene: Phaser.Scene;
  /** True while a "blocking" interaction is in progress (sit, modal). */
  isBlocked: () => boolean;
  setBlocked: (blocked: boolean) => void;
  /** Player physics body — used by sit-action to lock the player. */
  playerBody: Phaser.GameObjects.Rectangle & { body: Phaser.Physics.Arcade.Body };
  /** Fire-and-forget particle burst at (x, y) with a tint and quantity. */
  burstParticles: (x: number, y: number, tint: number, quantity?: number) => void;
  /** Float a small text message upward from (x, y) and fade out. */
  floatText: (x: number, y: number, text: string, color?: string) => void;
  /** Brief screen tint, e.g. warm gold for "drink alcohol". */
  flashScreen: (color: number, duration?: number) => void;
}

const HINT_DEPTH = 220;
const PROMPT_BG_ALPHA = 0.85;

export class InteractionManager {
  private items: Interactable[] = [];
  private hint: Phaser.GameObjects.Container;
  private hintBg: Phaser.GameObjects.Graphics;
  private hintText: Phaser.GameObjects.Text;
  private currentNearest?: Interactable;
  private blocked = false;
  private ctx!: InteractionContext;

  constructor(
    private scene: Phaser.Scene,
    private playerBody: Phaser.GameObjects.Rectangle & { body: Phaser.Physics.Arcade.Body },
  ) {
    // Floating "[E] {label}" hint — single shared instance, repositioned each
    // frame to hover over the nearest interactable. Hidden by default.
    this.hintBg = scene.add.graphics();
    this.hintText = scene.add.text(0, 0, "", {
      fontFamily: "Inter, system-ui, sans-serif",
      fontSize: "11px",
      fontStyle: "600",
      color: "#e2e8f0",
    }).setOrigin(0.5, 0.5);
    this.hint = scene.add.container(0, 0, [this.hintBg, this.hintText]);
    this.hint.setDepth(HINT_DEPTH).setVisible(false).setAlpha(0);

    // E key — primary interact button
    scene.input.keyboard!.on("keydown-E", () => this.tryInteract());

    this.ctx = {
      scene,
      isBlocked: () => this.blocked,
      setBlocked: (b: boolean) => { this.blocked = b; },
      playerBody: this.playerBody,
      burstParticles: this.burstParticles.bind(this),
      floatText: this.floatText.bind(this),
      flashScreen: this.flashScreen.bind(this),
    };

    this.ensureParticleTexture();
  }

  register(item: Interactable) { this.items.push(item); }

  /** Called every frame from MainScene.update(). */
  tick() {
    const px = this.playerBody.x;
    const py = this.playerBody.y;
    let best: Interactable | undefined;
    let bestDist = Infinity;
    for (const it of this.items) {
      if (it.available && !it.available(this.ctx, it)) continue;
      const dx = it.x - px;
      const dy = it.y - py;
      const d2 = dx * dx + dy * dy;
      if (d2 < it.radius * it.radius && d2 < bestDist) {
        best = it;
        bestDist = d2;
      }
    }

    if (best !== this.currentNearest) {
      this.currentNearest = best;
      if (best) this.showHint(best);
      else this.hideHint();
    } else if (best) {
      // Reposition hint over the nearest item (in case it moves)
      this.positionHint(best);
    }
  }

  /** Public so the mobile "E" button can fire an interaction outside of keyboard. */
  tryInteract() {
    if (!this.currentNearest) return;
    this.currentNearest.onInteract(this.ctx, this.currentNearest);
  }

  private showHint(it: Interactable) {
    const text = it.dynamicLabel ? it.dynamicLabel(it) : it.label;
    this.hintText.setText(`[E] ${text}`);
    this.positionHint(it);
    this.hint.setVisible(true);
    this.scene.tweens.killTweensOf(this.hint);
    this.scene.tweens.add({
      targets: this.hint,
      alpha: 1,
      y: { from: it.y - 36, to: it.y - 42 },
      duration: 200,
      ease: "Sine.out",
    });
  }

  private hideHint() {
    this.scene.tweens.killTweensOf(this.hint);
    this.scene.tweens.add({
      targets: this.hint,
      alpha: 0,
      duration: 150,
      ease: "Sine.in",
      onComplete: () => this.hint.setVisible(false),
    });
  }

  private positionHint(it: Interactable) {
    const text = it.dynamicLabel ? it.dynamicLabel(it) : it.label;
    if (this.hintText.text !== `[E] ${text}`) {
      this.hintText.setText(`[E] ${text}`);
    }
    const padX = 10;
    const padY = 6;
    const w = this.hintText.width + padX * 2;
    const h = this.hintText.height + padY * 2;

    this.hintBg.clear();
    this.hintBg.fillStyle(0x0f172a, PROMPT_BG_ALPHA);
    this.hintBg.fillRoundedRect(-w / 2, -h / 2, w, h, 8);
    this.hintBg.lineStyle(1, 0x22d3ee, 0.55);
    this.hintBg.strokeRoundedRect(-w / 2, -h / 2, w, h, 8);

    this.hint.x = it.x;
    this.hint.y = it.y - 42;
  }

  // ─────────────────────── shared interaction helpers ───────────────────────

  private ensureParticleTexture() {
    if (this.scene.textures.exists("interact-spark")) return;
    const g = this.scene.make.graphics({ x: 0, y: 0, add: false });
    g.fillStyle(0xffffff, 1);
    g.fillCircle(3, 3, 3);
    g.generateTexture("interact-spark", 6, 6);
    g.destroy();
  }

  private burstParticles(x: number, y: number, tint: number, quantity = 14) {
    const burst = this.scene.add.particles(x, y, "interact-spark", {
      lifespan: 700,
      speed: { min: 40, max: 110 },
      angle: { min: 230, max: 310 },         // upward fan
      scale: { start: 1.0, end: 0 },
      alpha: { start: 1, end: 0 },
      tint,
      blendMode: "ADD",
      quantity,
      emitting: false,
    });
    burst.setDepth(120);
    burst.explode(quantity);
    this.scene.time.delayedCall(900, () => burst.destroy());
  }

  private floatText(x: number, y: number, text: string, color = "#22d3ee") {
    const t = this.scene.add.text(x, y - 20, text, {
      fontFamily: "Inter, system-ui, sans-serif",
      fontSize: "13px",
      fontStyle: "700",
      color,
      stroke: "#0b1120",
      strokeThickness: 3,
    }).setOrigin(0.5, 1).setDepth(125);
    this.scene.tweens.add({
      targets: t,
      y: y - 64,
      alpha: { from: 1, to: 0 },
      duration: 1400,
      ease: "Sine.out",
      onComplete: () => t.destroy(),
    });
  }

  private flashScreen(color: number, duration = 350) {
    const cam = this.scene.cameras.main;
    cam.flash(duration, (color >> 16) & 0xff, (color >> 8) & 0xff, color & 0xff, true);
  }
}
